import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { countyLabel } from "@/lib/county-label";
import { useRouter } from "@tanstack/react-router";
import { Keyboard } from "lucide-react";
// Statically imported on purpose: /map's whole job is to render this, so the state
// layer is server-rendered into the HTML. That is what makes the map appear on first
// paint, work with JavaScript disabled (every state is a real <a> to its page), and
// be crawlable. County geometry, the county search index and the species layer stay
// dynamic — see src/lib/geo/county-shapes.ts.
import { US_GEO } from "@/lib/us-geo";
import { STATES, countyPagesEnabled, type StateInfo } from "@/lib/states";
import { CARD_SURFACE } from "@/components/page-shell";
import { cn } from "@/lib/utils";
import {
  DEFAULT_VIEW,
  MAX_SCALE,
  MIN_SCALE,
  boundsOfPath,
  boundsOverlap,
  clampView,
  fitBounds,
  visibleBounds,
  type Bounds,
  type MapView,
} from "@/lib/map/view";
import { bucketFor, buildSyncLayer, loadSpeciesLayer, type LayerData } from "@/lib/map/layers";
import { layerLabel, type MapLayer, type MapSearch } from "@/lib/map/search-params";
import {
  hasCountyChunk,
  loadCountyIndex,
  loadCountyShapes,
  type CountyIndexEntry,
  type CountyShape,
} from "@/lib/geo/county-shapes";
import type { LocationMatch } from "@/lib/location";
import { track } from "@/lib/analytics";
import { useMapView } from "./use-map-view";
import { MapCanvas, type CanvasFeature } from "./map-canvas";
import { MapKey, MapLegend } from "./map-legend";
import { MapLayerSwitcher } from "./map-layer-switcher";
import { MapSearch as MapSearchBox } from "./map-search";
import { MapDetail, MapDetailEmpty, type SelectedCounty } from "./map-detail";
import { MapFeatureList } from "./map-feature-list";
import { MapBackControl, MapStepControls, MapZoomControls } from "./map-controls";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { LG_QUERY, useMediaQuery } from "@/hooks/use-media-query";

const BY_ABBR = new Map<string, StateInfo>(STATES.map((s) => [s.abbr, s]));
const BY_SLUG = new Map<string, StateInfo>(STATES.map((s) => [s.slug, s]));

const KEYBOARD_HELP =
  "Drag to pan, or focus the map and use the arrow keys. Press plus and minus to zoom, and 0 to return to the whole country. Tab moves to a state; the arrow keys then step between states and Enter opens one.";

function stateBounds(abbr: string): Bounds | null {
  const g = US_GEO.find((s) => s.abbr === abbr);
  return g ? (g.bbox as Bounds) : null;
}

/** The camera position a given URL describes. */
function viewForSearch(search: MapSearch): MapView {
  const info = search.state ? BY_SLUG.get(search.state) : undefined;
  const bounds = info ? stateBounds(info.abbr) : null;
  const base = bounds ? fitBounds(bounds) : DEFAULT_VIEW;
  return search.z ? clampView({ ...base, scale: search.z }) : base;
}

export interface InteractiveMapProps {
  search: MapSearch;
  /** Push a new view into the URL (browser back then works as an undo). */
  onNavigate: (next: Partial<MapSearch>) => void;
  /** Replace the current URL — used for zoom, which must not fill up history. */
  onReplace: (next: Partial<MapSearch>) => void;
}

export function InteractiveMap({ search, onNavigate, onReplace }: InteractiveMapProps) {
  const [mounted, setMounted] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [countyIndex, setCountyIndex] = useState<CountyIndexEntry[] | null>(null);
  const [counties, setCounties] = useState<{ abbr: string; shapes: CountyShape[] } | null>(null);
  const [loadingCounties, setLoadingCounties] = useState(false);
  const router = useRouter();
  const isDesktop = useMediaQuery(LG_QUERY);
  const camera = useMapView(viewForSearch(search));
  const { view, flyTo, flyToBounds, reset, zoomIn, zoomOut } = camera;

  useEffect(() => setMounted(true), []);

  const selectedState = search.state ? (BY_SLUG.get(search.state) ?? null) : null;

  // Three layers are computed from data the map already has. The species layer needs
  // ~60KB of field-guide prose, so it is fetched only if someone asks for it — and
  // until it arrives the map keeps painting the layer it was already showing, rather
  // than flashing a legend that doesn't match the colours.
  const [speciesLayer, setSpeciesLayer] = useState<LayerData | null>(null);
  useEffect(() => {
    if (search.layer !== "species" || speciesLayer) return;
    let cancelled = false;
    void loadSpeciesLayer().then((l) => {
      if (!cancelled) setSpeciesLayer(l);
    });
    return () => {
      cancelled = true;
    };
  }, [search.layer, speciesLayer]);

  const resolvedLayer: LayerData | null = useMemo(
    () => (search.layer === "species" ? speciesLayer : buildSyncLayer(search.layer)),
    [search.layer, speciesLayer],
  );
  const lastLayer = useRef<LayerData>(buildSyncLayer("awareness"));
  if (resolvedLayer) lastLayer.current = resolvedLayer;
  const layer = resolvedLayer ?? lastLayer.current;
  const layerLoading = resolvedLayer === null;

  // Drill-down: one state's counties, fetched only when that state is the one on
  // screen. 51 chunks exist; at most one is ever in memory.
  useEffect(() => {
    const abbr = selectedState?.abbr;
    if (!abbr || !hasCountyChunk(abbr)) {
      setCounties(null);
      setLoadingCounties(false);
      return;
    }
    if (counties?.abbr === abbr) return;
    let cancelled = false;
    setLoadingCounties(true);
    void loadCountyShapes(abbr).then((shapes) => {
      if (cancelled) return;
      setCounties({ abbr, shapes });
      setLoadingCounties(false);
    });
    return () => {
      cancelled = true;
    };
  }, [selectedState?.abbr, counties?.abbr]);

  // --- Features -------------------------------------------------------------
  const features: CanvasFeature[] = useMemo(
    () =>
      US_GEO.map((g) => {
        const info = BY_ABBR.get(g.abbr);
        const bucket = bucketFor(layer, g.abbr);
        return {
          id: g.abbr,
          name: g.name,
          d: g.d,
          bbox: g.bbox as Bounds,
          href: info ? `/states/${info.slug}` : "/states",
          bucket,
          ariaLabel: `${g.name}: ${bucket.label}. ${layerLabel[layer.id]}.`,
        };
      }),
    [layer],
  );

  const countyFeatures: CanvasFeature[] = useMemo(() => {
    if (!selectedState || !counties || counties.abbr !== selectedState.abbr) return [];
    const bucket = bucketFor(layer, selectedState.abbr);
    const pagesExist = countyPagesEnabled(selectedState);
    return counties.shapes.map((c) => ({
      id: c.fips,
      name: countyLabel({ name: c.name, fips: c.fips, stateAbbr: selectedState.abbr }),
      d: c.d,
      bbox: boundsOfPath(c.d),
      // Only link to a county page that exists; otherwise the state page is the
      // honest destination. The map must never offer a 404.
      href: pagesExist
        ? `/states/${selectedState.slug}/${c.slug}`
        : `/states/${selectedState.slug}`,
      bucket,
      ariaLabel: `${countyLabel({ name: c.name, fips: c.fips, stateAbbr: selectedState.abbr })}, ${selectedState.name}: ${bucket.label} (${selectedState.name}'s statewide level).`,
    }));
  }, [counties, selectedState, layer]);

  const drilled = countyFeatures.length > 0;
  const activeFeatures = drilled ? countyFeatures : features;
  const contextPaths = useMemo(
    () => (drilled ? US_GEO.filter((g) => g.abbr !== selectedState?.abbr).map((g) => g.d) : []),
    [drilled, selectedState?.abbr],
  );

  const inView = useMemo(() => {
    const bounds = visibleBounds(view);
    return activeFeatures.filter((f) => boundsOverlap(f.bbox, bounds));
  }, [activeFeatures, view]);

  // --- URL <-> camera -------------------------------------------------------
  // Fly only when the *place* in the URL changes (a click here, or the browser's
  // back button). Zoom changes write back to the URL but must not fly the camera,
  // or the two would chase each other.
  const appliedPlace = useRef<string | null>(search.state ?? null);
  useEffect(() => {
    const place = search.state ?? null;
    if (place === appliedPlace.current) return;
    appliedPlace.current = place;
    const info = place ? BY_SLUG.get(place) : undefined;
    const bounds = info ? stateBounds(info.abbr) : null;
    if (bounds) flyToBounds(bounds);
    else reset();
    if (info) setActiveId(info.abbr);
  }, [search.state, flyToBounds, reset]);

  // Mirror the zoom into the URL so any view can be linked, but debounced and as a
  // replace — a shared link should restore the view, not a hundred history entries.
  const lastWrittenZoom = useRef(search.z);
  useEffect(() => {
    if (!mounted) return;
    const rounded = Math.round(view.scale * 100) / 100;
    if (rounded === lastWrittenZoom.current) return;
    const id = window.setTimeout(() => {
      lastWrittenZoom.current = rounded;
      onReplace({ z: rounded > MIN_SCALE ? rounded : undefined });
    }, 400);
    return () => window.clearTimeout(id);
  }, [view.scale, mounted, onReplace]);

  // --- Actions --------------------------------------------------------------
  const selectFeature = useCallback(
    (feature: CanvasFeature) => {
      setActiveId(feature.id);
      if (!drilled) {
        const info = BY_ABBR.get(feature.id);
        if (!info) return;
        track("map_state_selected", { state: info.name, layer: search.layer });
        onNavigate({ state: info.slug, z: undefined });
        return;
      }
      // Inside a state: the first activation previews the county in the panel (and
      // puts it in the URL, so the view is shareable). Activating the county that is
      // already selected opens its page — the same place its href points to, which
      // is also what a no-JS click does on the first try.
      const shape = counties?.shapes.find((c) => c.fips === feature.id);
      if (!shape || !selectedState) return;
      if (search.county === shape.slug) {
        void router.navigate({ href: feature.href });
        return;
      }
      track("map_county_selected", { state: selectedState.name, county: shape.name });
      onNavigate({ state: selectedState.slug, county: shape.slug });
    },
    [drilled, counties, selectedState, search.county, search.layer, onNavigate, router],
  );

  const stepActive = useCallback(
    (delta: number) => {
      if (!activeFeatures.length) return;
      const index = activeFeatures.findIndex((f) => f.id === activeId);
      const next = activeFeatures[(index + delta + activeFeatures.length) % activeFeatures.length];
      setActiveId(next.id);
      const bounds = next.bbox;
      // Bring it into view if the step walked off screen, but don't re-frame the
      // whole map when it is already visible.
      if (!boundsOverlap(bounds, visibleBounds(view))) flyToBounds(bounds);
    },
    [activeFeatures, activeId, view, flyToBounds],
  );

  const pickLocation = useCallback(
    (match: LocationMatch) => {
      const info = match.state ?? BY_ABBR.get(match.stateAbbr);
      if (!info) return;
      track("map_search_used", { method: match.method, state: info.name });
      setActiveId(info.abbr);
      onNavigate({ state: info.slug, county: match.county?.slug, z: undefined });
    },
    [onNavigate],
  );

  const needCounties = useCallback(() => {
    if (countyIndex) return;
    void loadCountyIndex().then(setCountyIndex);
  }, [countyIndex]);

  const clearSelection = useCallback(() => {
    onNavigate({ state: undefined, county: undefined, z: undefined });
    flyTo(DEFAULT_VIEW);
  }, [onNavigate, flyTo]);

  const selectedCounty: SelectedCounty | null = useMemo(() => {
    if (!search.county || !counties) return null;
    const shape = counties.shapes.find((c) => c.slug === search.county);
    return shape ? { fips: shape.fips, name: shape.name, slug: shape.slug } : null;
  }, [search.county, counties]);

  // Frame a county that arrived from the URL (a shared link, or browser-back).
  const appliedCounty = useRef<string | null>(search.county ?? null);
  useEffect(() => {
    const key = search.county ?? null;
    if (key === appliedCounty.current) return;
    appliedCounty.current = key;
    if (!key) return;
    const shape = counties?.shapes.find((c) => c.slug === key);
    if (shape) {
      setActiveId(shape.fips);
      flyToBounds(boundsOfPath(shape.d), 0.3);
    }
  }, [search.county, counties, flyToBounds]);

  // --- Announcements --------------------------------------------------------
  const unit = drilled ? "counties" : "states";
  const announcement = selectedCounty
    ? `${countyLabel({ ...selectedCounty, stateAbbr: selectedState?.abbr ?? "" })}, ${selectedState?.name} selected. ${bucketFor(layer, selectedState?.abbr ?? "").label}.`
    : selectedState
      ? `${selectedState.name} selected. ${bucketFor(layer, selectedState.abbr).label}. ${
          drilled ? `${countyFeatures.length} counties loaded.` : `${inView.length} states in view.`
        }`
      : `Showing the whole United States. ${inView.length} states in view.`;

  const detail = selectedState ? (
    <MapDetail
      state={selectedState}
      county={selectedCounty}
      layer={layer}
      onClearCounty={() => onNavigate({ state: selectedState.slug, county: undefined })}
    />
  ) : (
    <MapDetailEmpty hint="Tap a state, or search a ZIP code or city. You'll see whether Lone Star ticks live there, when they peak, and what to do after a bite." />
  );

  return (
    <div className="grid grid-cols-[minmax(0,1fr)] gap-5 lg:grid-cols-[minmax(0,1fr)_20rem]">
      <div className="min-w-0 space-y-4">
        <MapSearchBox onPick={pickLocation} onNeedCounties={needCounties} counties={countyIndex} />

        <MapLayerSwitcher
          value={search.layer}
          onChange={(next: MapLayer) => onReplace({ layer: next })}
          loading={layerLoading}
        />

        <div className={cn(CARD_SURFACE, "relative overflow-hidden p-0")}>
          {/* Aspect ratio is reserved so the map never shifts the page as it loads. */}
          <div className="map-grid-bg relative aspect-[960/600] w-full bg-secondary/30">
            <MapCanvas
              features={activeFeatures}
              contextPaths={contextPaths}
              buckets={layer.buckets}
              camera={camera}
              selectedId={selectedState?.abbr ?? null}
              activeId={activeId}
              onSelect={selectFeature}
              onActivate={setActiveId}
              onStep={stepActive}
              label={
                drilled && selectedState
                  ? `Map of ${selectedState.name} counties, coloured by ${layerLabel[layer.id].toLowerCase()}`
                  : `Map of Lone Star tick ${layerLabel[layer.id].toLowerCase()} by U.S. state`
              }
              describedBy="map-keyboard-help"
            />
            {mounted && selectedState && (
              <MapBackControl label="Back to U.S." onClick={clearSelection} />
            )}
            {loadingCounties && (
              <p
                role="status"
                className="absolute left-1/2 top-3 -translate-x-1/2 rounded-sm border border-border bg-card px-3 py-1.5 text-xs font-medium text-muted-foreground shadow-card"
              >
                Loading counties…
              </p>
            )}
            {mounted && (
              <MapZoomControls
                onZoomIn={zoomIn}
                onZoomOut={zoomOut}
                onReset={clearSelection}
                canZoomIn={view.scale < MAX_SCALE}
                canZoomOut={view.scale > MIN_SCALE}
                atDefault={view.scale === MIN_SCALE && !selectedState}
              />
            )}
          </div>
        </div>

        <div className="lg:hidden">
          <MapKey layer={layer} />
        </div>

        <p id="map-keyboard-help" className="sr-only">
          {KEYBOARD_HELP}
        </p>
        <p aria-live="polite" className="sr-only">
          {announcement}
        </p>

        <div className="flex flex-wrap items-center justify-between gap-3">
          {mounted && (
            <MapStepControls
              onPrevious={() => stepActive(-1)}
              onNext={() => stepActive(1)}
              label={activeFeatures.find((f) => f.id === activeId)?.name ?? `Step through ${unit}`}
            />
          )}
          <p className="hidden items-center gap-1.5 text-xs text-muted-foreground lg:flex">
            <Keyboard className="h-3.5 w-3.5" aria-hidden />
            Arrows pan · <kbd>+</kbd>/<kbd>−</kbd> zoom · <kbd>0</kbd> resets
          </p>
        </div>

        <MapFeatureList
          features={inView}
          title={
            drilled && selectedState ? `${selectedState.name} counties in view` : "States in view"
          }
          emptyHint={`Zoom out to bring ${unit} back into view.`}
          onHighlight={setActiveId}
        />
      </div>

      {/* lg+: a persistent sidebar. Below that the same content becomes a bottom
          sheet, because a 20rem column on a phone is a column of two-word lines. */}
      <aside
        className="hidden h-fit border-t-2 border-rule pt-5 lg:sticky lg:top-24 lg:block"
        aria-label="Map details"
      >
        {detail}
        <hr className="my-5 border-border" />
        <MapLegend layer={layer} />
      </aside>

      <details className="group border-y border-border py-1 lg:hidden">
        <summary className="list-none flex min-h-11 cursor-pointer items-center justify-between text-sm font-semibold text-foreground">
          What each colour means
          <span className="text-sm font-semibold text-primary group-open:hidden">Show</span>
          <span className="hidden text-sm font-semibold text-primary group-open:inline">Hide</span>
        </summary>
        <div className="pb-4 pt-2">
          <MapLegend layer={layer} idPrefix="legend-sheet" />
        </div>
      </details>

      {!isDesktop && (
        <Drawer open={Boolean(selectedState)} onOpenChange={(open) => !open && clearSelection()}>
          <DrawerContent className="max-h-[85vh]">
            <DrawerHeader className="pb-0 text-left">
              <DrawerTitle className="sr-only">
                {selectedState ? selectedState.name : "Map details"}
              </DrawerTitle>
              <DrawerDescription className="sr-only">
                Whether Lone Star ticks live here, peak season and next steps.
              </DrawerDescription>
            </DrawerHeader>
            <div className="overflow-y-auto overscroll-contain px-4 pb-8 pt-2">{detail}</div>
          </DrawerContent>
        </Drawer>
      )}
    </div>
  );
}
